import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { setSelectedTemplate } from '../../features/templates/templatesSlice';
import { useGetTemplatesQuery, Industry, Orientation, DesignStyle, PrimaryColor, AnimationType } from '../../features/templates/templatesApi';
import { Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';




export default function TemplatesPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activeCategory, setActiveCategory] = useState("All Templates");
  const [activeOrientation, setActiveOrientation] = useState<Orientation | null>(null);
  const [activeStyle, setActiveStyle] = useState<DesignStyle | null>(null);
  const [activeColor, setActiveColor] = useState<PrimaryColor | null>(null);
  const [activeIndustry] = useState<Industry | null>(null);
  const [activeTemplateStyle] = useState<AnimationType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);


  const { data: templatesData, isLoading, isFetching } = useGetTemplatesQuery({
    search: searchQuery || undefined,
    category: activeCategory === "All Templates" ? undefined : activeCategory,
    orientation: activeOrientation || undefined,
    designStyle: activeStyle || undefined,
    primaryColor: activeColor || undefined,
    industry: activeIndustry || undefined,
    animationType: activeTemplateStyle || undefined,
    page,
    limit: 12
  });

  // Handle template click
  const handleTemplateClick = (template: any) => {
    dispatch(setSelectedTemplate({
      id: template._id,
      name: template.name,
      previewUrl: template.thumbnail,
      width: template.data.width,
      height: template.data.height,
      layers: template.data.layers,
      elements: template.data.elements,
      layerPositions: template.data.layerPositions,
      textContents: template.data.textContents,
      textStyles: template.data.textStyles,
      imageSettings: template.data.imageSettings,
      shapeSettings: template.data.shapeSettings,
      backgroundSettings: template.data.backgroundSettings,
      scrollerSettings: template.data.scrollerSettings
    }));
    navigate('/template-editor');
  };


  const gridItemClass = "w-full h-[180px] relative rounded-[8px] overflow-hidden group cursor-pointer border border-gray-100 shadow-sm hover:shadow-md transition-all";

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-[400px] w-full gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#2C9944]" />
          <p className="text-gray-500 font-medium">Loading templates...</p>
        </div>
      );
    }

    const templates = templatesData?.data || [];

    if (templates.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-[400px] w-full text-center">
          <div className="bg-gray-50 p-6 rounded-full mb-4">
            <Search className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No templates found</h3>
          <p className="text-gray-500 max-w-xs">Try adjusting your filters or search terms to find what you're looking for.</p>
          <button
            onClick={() => {
              setActiveCategory("All Templates");
              setActiveOrientation(null);
              setActiveStyle(null);
              setActiveColor(null);
              setSearchQuery("");
            }}
            className="mt-6 text-[#2C9944] font-semibold hover:underline"
          >
            Clear all filters
          </button>
        </div>
      );
    }

    return (
      <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full mb-8">
        <div className="flex items-center justify-between w-full">
          <p className="font-sans font-bold text-[#101928] text-[20px]">
            {activeCategory} ({templatesData?.meta.total || 0})
          </p>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-[#2C9944]" />}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-[20px] w-full">
          {templates.map((template) => (
            <div
              key={template._id}
              className={gridItemClass}
              onClick={() => handleTemplateClick(template)}
            >
              <img
                src={template.thumbnail}
                alt={template.name}
                className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/60 to-transparent translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                <p className="text-white text-xs font-semibold truncate">{template.name}</p>
                <div className="flex gap-1 mt-1">
                  <span className="text-[10px] bg-white/20 text-white px-1.5 py-0.5 rounded-sm backdrop-blur-sm">{template.category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {templatesData && templatesData.meta.total > templatesData.meta.limit && (
          <div className="flex items-center justify-center w-full gap-4 mt-8 pb-10">
            <button
              disabled={page === 1 || isFetching}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="p-2 rounded-full border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-gray-600">
              Page {page} of {Math.ceil(templatesData.meta.total / templatesData.meta.limit)}
            </span>
            <button
              disabled={page >= Math.ceil(templatesData.meta.total / templatesData.meta.limit) || isFetching}
              onClick={() => setPage(p => p + 1)}
              className="p-2 rounded-full border border-gray-200 disabled:opacity-30 hover:bg-gray-50 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-1 gap-[24px] overflow-hidden size-full">

        {/* Right Content */}
        <div className="flex-1 flex flex-col gap-[24px] overflow-hidden w-full">
          <div className="flex items-center justify-between w-full">
            {/* Search Bar */}
            <div className="bg-white content-stretch flex h-[44px] items-center overflow-clip px-[14px] py-[8px] relative rounded-[8px] shadow-sm shrink-0 w-full max-w-[320px] border border-gray-200 transition-colors focus-within:border-[#0ba29b]">
              <div className="flex items-center gap-2 w-full">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="flex-1 bg-transparent border-none outline-none text-[14px] font-sans text-gray-900 placeholder:text-gray-400 pl-1"
                />
              </div>
            </div>
          </div>
          {/* Main List */}
          <div className="flex-1 overflow-y-auto w-full rounded-[12px] bg-white shadow-sm border border-gray-100 p-[32px]">
            {renderContent()}
          </div>
      </div>
    </div>
  );
}
